import shortid from 'shortid'

import { wsUrl } from 'environment'
import { getToken } from 'utils/token.utils'

import { store } from 'redux/store'
import { SET_PLAYTESTER_V2_STATE } from 'redux/playtesterV2/actions/types'

import { WebsocketV2Resposne, MessageType } from 'services/apiTypes/playtester.types'
import { generateSharablePlaytesterState } from 'redux/playtesterV2/helpers/sharablePlaytesterHelper'
import ToastService from './toast.service'

class PlayteserService {
  roomId: string | null
  clientId: string // Unique identifier for this instance of the site (tab, browser, device, etc)
  isHost: boolean

  connection: WebSocket | null
  onCloseCallback: null | (() => void)

  constructor() {
    this.roomId = null
    this.connection = null
    this.clientId = shortid.generate()
    this.isHost = false
    this.onCloseCallback = null
  }

  initialize = (roomId: string, isHost: boolean, onCloseCallback?: () => void) => {
    this.roomId = roomId
    this.isHost = isHost

    if (this.connection) return Promise.reject("You're already hosting a room, no need to connect again")

    return this.connect().then(() => {
      if (onCloseCallback) this.onCloseCallback = onCloseCallback
    })
  }

  uninitialize = () => {
    this.connection?.close()

    this.connection = null
    this.roomId = null
  }

  connect = () => {
    if (!this.roomId) return Promise.resolve() // Sandbox mode, no need for collaborative connection

    return new Promise(async (resolve, reject) => {
      if (!this.roomId) return reject('No room id provided')

      const token = await getToken()
      const connectionUrl = `${wsUrl}/api/ws/playtester/${this.roomId}/?clientId=${this.clientId}&token=${token}`

      this.connection = new WebSocket(connectionUrl)

      this.connection.onmessage = this.onMessage

      this.connection.onopen = () => this.onOpen(resolve)
      this.connection.onclose = this.onClose

      this.connection.onerror = err => this.onError(err, reject)
    })
  }

  send = (payload: any, reduxActionType: MessageType, attemptConnection = true): Promise<void> => {
    if (!this.isHost) return Promise.resolve()
    if (!this.connection) return Promise.resolve()

    // prettier-ignore
    // if (!this.connection && attemptConnection) return this.connect()?.then(() => this.send(payload, reduxActionType, false))
    // if (!this.connection) return Promise.reject()

    this.connection.send(JSON.stringify({ payload, type: reduxActionType }))

    return Promise.resolve()
  }

  onMessage = (response: MessageEvent) => {
    try {
      const ws: WebsocketV2Resposne = JSON.parse(response.data)

      const status = ws?.status
      const action = ws?.action
      const generatedBySelf = ws?.userInfo?.clientId === this.clientId

      if (generatedBySelf) return

      if (action === 'SET_PLAYTESTER_V2_STATE')
        return store.dispatch({ type: SET_PLAYTESTER_V2_STATE, payload: ws.payload })

      if (action === 'SET_DECK_STATE' && this.isHost)
        return this.send(generateSharablePlaytesterState(store.getState().playtesterV2), 'SET_PLAYTESTER_V2_STATE')
    } catch (err) {
      console.error(err)
    }
  }

  onOpen = (resolve: any) => {
    resolve()
  }

  onClose = () => {
    this.connection?.close()
    this.connection = null

    if (this.onCloseCallback) this.onCloseCallback()
  }

  onError = (err: any, reject: any) => {
    if (this.onCloseCallback) this.onCloseCallback()

    this.connection?.close()
    this.connection = null
    this.onCloseCallback = null

    reject(err)
  }
}

const playteserService = new PlayteserService()

export default playteserService
